import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IContaBancaria, INfsePolitica, IPlanoContaOpcao, ISetupCategoria, ISetupVersao, SetupStatus } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import ModalCategoriaSetup, { ModoCategoriaSetup } from './modal-categoria-setup';

interface Props {
    esteiraId: number;
    contas: IContaBancaria[];
    /** Salvar uma categoria muda a situação da etapa. */
    onAlterou?: () => void;
}

const STATUS: Record<SetupStatus, { label: string; classe: string }> = {
    OK: { label: 'OK', classe: 'badge-light-success' },
    INCOMPLETA: { label: 'Incompleta', classe: 'badge-light-warning' },
    CONTA_FORA_DO_PLANO: { label: 'Conta fora do plano', classe: 'badge-light-danger' },
    INATIVA: { label: 'Inativa', classe: 'badge-light' },
};

const dataBr = (valor: string | null) => (valor ? moment(valor).format('DD/MM/YYYY') : 'desde sempre');

function Status({ versao, status, aviso }: { versao?: ISetupVersao | null; status: SetupStatus; aviso: string | null }) {
    const s = STATUS[status];
    return (
        <>
            <span className={clsx('badge', s.classe)}>{s.label}</span>
            {aviso && <i className='fas fa-exclamation-triangle text-warning ms-2' title={aviso}></i>}
            {versao && <span className='text-muted fs-8 ms-2'>v{versao.versao}</span>}
        </>
    );
}

// DEVN3-768 — Setup bancário (E2): para qual conta contábil vai a contrapartida de cada categoria
// recorrente do extrato. A situação é calculada contra o plano de contas do período escolhido.
export default function SetupBancario({ esteiraId, contas, onAlterou }: Props) {
    const [categorias, setCategorias] = useState<ISetupCategoria[]>([]);
    const [plano, setPlano] = useState<IPlanoContaOpcao[]>([]);
    const [carregando, setCarregando] = useState(false);
    const [modal, setModal] = useState<ModoCategoriaSetup | null>(null);
    const [historico, setHistorico] = useState<{ categoria: ISetupCategoria; versoes: ISetupVersao[] } | null>(null);
    const [politicaNfse, setPoliticaNfse] = useState<INfsePolitica | null>(null);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try {
            setCategorias(await ModuloBancarioService.setup(esteiraId));
            onAlterou?.();
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível carregar o Setup bancário.', icon: 'error' });
        } finally {
            setCarregando(false);
        }
    }, [esteiraId, onAlterou]);

    useEffect(() => {
        buscar();
        ModuloBancarioService.planoContas(esteiraId).then(setPlano).catch(() => setPlano([]));
        ModuloBancarioService.politicaNfse(esteiraId).then(setPoliticaNfse).catch(() => setPoliticaNfse(null));
    }, [esteiraId, buscar]);

    const alternarProvisaoPeloLiquido = async () => {
        if (!politicaNfse) return;
        const pelo = !politicaNfse.provisaoPeloLiquido;
        const { isConfirmed } = await Swal.fire({
            text: pelo
                ? 'A empresa passa a provisionar serviços pelo líquido: a retenção da NFS-e não entra na minuta. As minutas regeneráveis são refeitas.'
                : 'A empresa passa a provisionar serviços pelo bruto: a NFS-e leva a retenção para a minuta. As minutas regeneráveis são refeitas.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Confirmar',
            cancelButtonText: 'Cancelar',
        });
        if (!isConfirmed) return;
        try {
            setPoliticaNfse(await ModuloBancarioService.salvarPoliticaNfse(esteiraId, pelo));
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível alterar a configuração.', icon: 'error' });
        }
    };

    const alterarSituacao = async (categoria: ISetupCategoria, ativa: boolean, contaBancariaId = 0) => {
        const alvo = contaBancariaId ? 'a exceção desta conta bancária' : `a categoria "${categoria.nome}"`;
        const { isConfirmed } = await Swal.fire({
            text: ativa ? `Reativar ${alvo}?` : `Inativar ${alvo}? Ela deixa de ser usada na geração, desde o início da versão atual. O histórico fica preservado.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: ativa ? 'Reativar' : 'Inativar',
            cancelButtonText: 'Cancelar',
        });
        if (!isConfirmed) return;
        try {
            await ModuloBancarioService.alterarSituacao(esteiraId, categoria.id, ativa, contaBancariaId);
            buscar();
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível alterar a situação.', icon: 'error' });
        }
    };

    const abrirHistorico = async (categoria: ISetupCategoria) => {
        try {
            setHistorico({ categoria, versoes: await ModuloBancarioService.versoesCategoria(esteiraId, categoria.id) });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível carregar o histórico.', icon: 'error' });
        }
    };

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Setup bancário
                    {carregando && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                <button className='btn btn-sm btn-primary' onClick={() => setModal({ tipo: 'nova' })}>
                    <i className='fas fa-plus me-1'></i>Nova categoria
                </button>
            </div>
            <div className='card-body pt-0'>
                <div className='text-muted fs-7 mb-3'>
                    Categorias recorrentes do extrato e a conta de contrapartida de cada uma. Salvar já aprova e grava uma nova versão; a anterior fica no histórico.
                </div>
                {politicaNfse && (
                    <div className='d-flex flex-wrap align-items-center gap-2 border rounded p-3 mb-3 fs-7'>
                        <span className='flex-grow-1'>
                            Serviços provisionados pelo <strong>{politicaNfse.provisaoPeloLiquido ? 'líquido' : 'bruto'}</strong>.{' '}
                            {politicaNfse.provisaoPeloLiquido ? 'A retenção da NFS-e não entra na minuta.' : 'A NFS-e leva a retenção para a minuta.'}
                            {politicaNfse.versao > 0 && <span className='text-muted fs-8 ms-2'>v{politicaNfse.versao}</span>}
                        </span>
                        <button className='btn btn-sm btn-light' onClick={alternarProvisaoPeloLiquido}>
                            Mudar para {politicaNfse.provisaoPeloLiquido ? 'bruto' : 'líquido'}
                        </button>
                    </div>
                )}
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                    <thead>
                        <tr className='fw-bold text-muted'>
                            <th>Categoria</th>
                            <th>Direção</th>
                            <th>Conta de contrapartida</th>
                            <th>Padrões de histórico</th>
                            <th>Vigência</th>
                            <th>Situação</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {categorias.map(c => {
                            const inativa = c.status === 'INATIVA';
                            return (
                                <tr key={c.id} className={clsx(inativa && 'opacity-50')}>
                                    <td>
                                        {c.nome}
                                        {c.codigoPadrao && <span className='badge badge-light ms-2'>padrão</span>}
                                        {c.excecoes.map(e => (
                                            <div key={e.contaBancariaId} className='fs-8 text-muted mt-1'>
                                                Exceção: {e.contaBancariaDescricao ?? `conta bancária ${e.contaBancariaId}`} → {e.contaContabil}{' '}
                                                <Status versao={e} status={e.status} aviso={e.aviso} />
                                                {e.ativa
                                                    ? <>
                                                        <a href='#' className='ms-2' onClick={ev => { ev.preventDefault(); setModal({ tipo: 'excecao', categoria: c, contaBancariaId: e.contaBancariaId }); }}>editar</a>
                                                        <a href='#' className='ms-2' onClick={ev => { ev.preventDefault(); alterarSituacao(c, false, e.contaBancariaId); }}>encerrar</a>
                                                    </>
                                                    : <a href='#' className='ms-2' onClick={ev => { ev.preventDefault(); alterarSituacao(c, true, e.contaBancariaId); }}>reabrir</a>}
                                            </div>
                                        ))}
                                    </td>
                                    <td>{c.direcao === 'ENTRADA' ? 'Entrada' : 'Saída'}</td>
                                    <td>
                                        {c.atual?.contaContabil
                                            ? <>{c.atual.contaContabil} <span className='text-muted'>{c.atual.contaDescricaoAtual ?? c.atual.contaDescricao}</span></>
                                            : <span className='text-muted'>—</span>}
                                    </td>
                                    <td className='fs-7'>{c.atual?.padroes.length ? c.atual.padroes.join(' · ') : <span className='text-muted'>—</span>}</td>
                                    <td>{c.atual ? dataBr(c.atual.vigenciaInicio) : '—'}</td>
                                    <td><Status versao={c.atual} status={c.status} aviso={c.aviso} /></td>
                                    <td className='text-end text-nowrap'>
                                        {!inativa && (
                                            <button className='btn btn-sm btn-icon btn-light me-1' title='Editar (nova versão)' onClick={() => setModal({ tipo: 'versao', categoria: c })}>
                                                <i className='fas fa-pen'></i>
                                            </button>
                                        )}
                                        {!inativa && contas.length > 0 && c.atual?.contaContabil && (
                                            <button className='btn btn-sm btn-icon btn-light me-1' title='Exceção por conta bancária' onClick={() => setModal({ tipo: 'excecao', categoria: c })}>
                                                <i className='fas fa-code-branch'></i>
                                            </button>
                                        )}
                                        <button className='btn btn-sm btn-icon btn-light me-1' title='Histórico de versões' onClick={() => abrirHistorico(c)}>
                                            <i className='fas fa-history'></i>
                                        </button>
                                        <button className='btn btn-sm btn-icon btn-light' title={inativa ? 'Reativar' : 'Inativar'} onClick={() => alterarSituacao(c, inativa)}>
                                            <i className={clsx('fas', inativa ? 'fa-undo' : 'fa-ban')}></i>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {modal && (
                <ModalCategoriaSetup
                    esteiraId={esteiraId}
                    modo={modal}
                    plano={plano}
                    contas={contas}
                    onHide={() => setModal(null)}
                    onSalvo={buscar}
                />
            )}

            {historico && (
                <Modal show onHide={() => setHistorico(null)} centered size='xl'>
                    <Modal.Header closeButton>
                        <Modal.Title>Histórico: {historico.categoria.nome}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {historico.versoes.length === 0 ? (
                            <div className='text-muted'>Nenhuma versão gravada: a categoria ainda não tem conta.</div>
                        ) : (
                            <table className='table table-row-dashed align-middle gs-0 gy-2'>
                                <thead>
                                    <tr className='fw-bold text-muted'>
                                        <th>Escopo</th>
                                        <th>Versão</th>
                                        <th>Conta</th>
                                        <th>Padrões</th>
                                        <th>Vigência</th>
                                        <th>Situação</th>
                                        <th>Gravada em</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historico.versoes.map(v => (
                                        <tr key={v.id}>
                                            <td>{v.contaBancariaId ? (v.contaBancariaDescricao ?? `Conta bancária ${v.contaBancariaId}`) : 'Empresa'}</td>
                                            <td>v{v.versao}</td>
                                            <td>{v.contaContabil ?? '—'} <span className='text-muted'>{v.contaDescricao}</span></td>
                                            <td className='fs-7'>{v.padroes.join(' · ') || '—'}</td>
                                            <td>{dataBr(v.vigenciaInicio)}</td>
                                            <td>{v.ativa ? 'Ativa' : 'Inativa'}</td>
                                            {/* O backend grava em UTC sem o "Z". */}
                                            <td>{moment.utc(v.dataCadastro).local().format('DD/MM/YYYY HH:mm')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
}
